import React, { useState, useEffect } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select, TableRow, Table, TableHead, TableBody, TableContainer, Paper, TableCell, Checkbox, FormControlLabel, ListItemText, Chip } from "@material-ui/core";
import ScheduleService from "./Locality/Service/scheduleService";
import SchoolService from "./Locality/Service/schoolService";
import * as Yup from 'yup';
import { Grid } from "@material-ui/core";
import { useFormik } from 'formik';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import Widget from "../../components/Widget/Widget";
import PageTitle from "../../components/PageTitle/PageTitle";
import { Typography } from "../../components/Wrappers/Wrappers";
import { showAppConfirm } from '../../context/AlertContext';

const PUBLISH_STATUS_OPTIONS = [
    { value: 'P', label: 'Only presented' },
    { value: 'WA', label: 'Only work with assistance' },
    { value: 'WI', label: 'Only work independently' },
    { value: 'M', label: 'Mastered' },
];

const DEFAULT_PUBLISH_STATUS_KEYS = PUBLISH_STATUS_OPTIONS.map((o) => o.value);

const publishStatusLabel = (key) =>
    PUBLISH_STATUS_OPTIONS.find((o) => o.value === key)?.label || key;

/**
 * schoolId comes back either populated or as a raw id, and a populated school
 * can still be missing its name. Always resolve to a renderable string.
 */
const getScheduleSchoolName = (scheduleItem, schoolList = []) => {
    const school = scheduleItem?.schoolId;
    if (!school) return 'N/A';

    const findNameById = (id) => {
        const match = schoolList.find((s) => String(s?._id) === String(id));
        return match?.schooleName || match?.schoolName || '';
    };

    if (typeof school === 'object') {
        return (
            school.schooleName ||
            school.schoolName ||
            school.name ||
            findNameById(school._id) ||
            'N/A'
        );
    }

    return findNameById(school) || String(school);
};

const StyledTableCell = withStyles((theme) => ({
    head: {
        backgroundColor: "#1a1a1aad",
        color: theme.palette.common.white,
    },
    body: {
        fontSize: 14,
    },
}))(TableCell);

const useStyles = makeStyles((theme) => ({
    container: {
        display: 'flex',
        flexWrap: 'wrap',
    },
    textField: {
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
        width: 200,
    },
    tableOverflow: {
        overflowX: 'auto',
    },
    chip: {
        margin: 2,
        height: 24,
        fontSize: '0.75rem',
    },
    chipsWrap: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 4,
    },
}));

export default function Schedule() {
    const tableHeaders = ['Frequency', 'School', 'Publish statuses', 'Notify before publish', 'Edit', 'Delete'];
    const classes = useStyles();
    const [scheduleList, setScheduleList] = useState([]);
    const [schoolList, setSchoolList] = useState([]);
    const [registeredSchoolsCount, setRegisteredSchoolsCount] = useState(0);
    const [registeredSchoolNames, setRegisteredSchoolNames] = useState([]);
    const [selectedRegisteredSchool, setSelectedRegisteredSchool] = useState("");
    const [open, setOpen] = useState(false);
    const [schedule, setSchedule] = useState({
        frequency: '',
        schoolId: '',
        isNotifyBeforePublish: false,
        publishStatusKeys: [...DEFAULT_PUBLISH_STATUS_KEYS],
    });

    const userDetails = localStorage.getItem("userDetail")
        ? JSON.parse(localStorage.getItem("userDetail"))
        : {};
    const currentSchoolId = userDetails?.schooleId || userDetails?.schoolId || userDetails?._id;
    const isSchoolRole = userDetails?.role === "SCHOOLE";
    
    useEffect(() => {
        getScheduleList();
        getSchoolList();
        // SCHOOLE users should only see their own schedules.
        // Avoid calling the global count API since we filter schedules client-side.
        if (!isSchoolRole) {
            getRegisteredSchoolsCount();
        }
        return () => {
            setScheduleList([]);
        }
    }, []);

    const getSchoolList = () => {
        // SCHOOLE should only see its own school in the popup
        if (isSchoolRole && currentSchoolId) {
            SchoolService.getAllSchoolList()
                .then((res) => {
                    const all = Array.isArray(res) ? res : [];
                    const onlyCurrent = all.filter((s) => String(s?._id) === String(currentSchoolId));
                    setSchoolList(onlyCurrent);
                })
                .catch((err) => {
                    console.error("Error fetching schools:", err);
                    setSchoolList([]);
                });
            return;
        }

        SchoolService.getAllSchoolList()
            .then((res) => {
                setSchoolList(res || []);
            })
            .catch((err) => {
                console.error("Error fetching schools:", err);
            });
    };

    const emptyScheduleForm = () => ({
        frequency: '',
        schoolId: isSchoolRole && currentSchoolId ? currentSchoolId : '',
        isNotifyBeforePublish: false,
        publishStatusKeys: [...DEFAULT_PUBLISH_STATUS_KEYS],
    });

    const handleOpen = (status) => {
        if(status){
            setSchedule(emptyScheduleForm());
        }
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };
    
    const getScheduleList = () => {
        ScheduleService.getAllSchedule().then((res) => {
            const schedules = Array.isArray(res) ? res : [];
            const filteredSchedules =
                isSchoolRole && currentSchoolId
                    ? schedules.filter((s) => {
                        const id = s?.schoolId?._id || s?.schoolId;
                        return String(id) === String(currentSchoolId);
                      })
                    : schedules;

            setScheduleList(filteredSchedules);
            // Extract unique registered school names
            getRegisteredSchoolNames(filteredSchedules);
        }).catch((err) => {
            console.error('Error fetching schedules:', err);
        });
    };

    // Get unique school names from schedule list
    const getRegisteredSchoolNames = (schedules) => {
        const uniqueSchools = new Map();
        schedules.forEach(scheduleItem => {
            const schoolId = scheduleItem.schoolId?._id || scheduleItem.schoolId;
            const schoolName = scheduleItem.schoolId?.schooleName || scheduleItem.schoolId?.schoolName;
            
            if (schoolId && schoolName) {
                const id = schoolId.toString();
                if (!uniqueSchools.has(id)) {
                    uniqueSchools.set(id, schoolName);
                }
            }
        });
        
        const schoolNamesArray = Array.from(uniqueSchools.values());
        setRegisteredSchoolNames(schoolNamesArray);
        setRegisteredSchoolsCount(schoolNamesArray.length);
    };

    const getRegisteredSchoolsCount = () => {
        ScheduleService.getRegisteredSchoolsCount().then((res) => {
            setRegisteredSchoolsCount(res.count || 0);
        }).catch((err) => {
            console.error('Error fetching registered schools count:', err);
            // Fallback to client-side calculation if API fails
            const uniqueSchoolIds = new Set();
            scheduleList.forEach(scheduleItem => {
                const schoolId = scheduleItem.schoolId?._id || scheduleItem.schoolId;
                if (schoolId) {
                    uniqueSchoolIds.add(schoolId.toString());
                }
            });
            setRegisteredSchoolsCount(uniqueSchoolIds.size || 0);
        });
    };

    const validationSchema = Yup.object().shape({
        frequency: Yup.string().required('Frequency is required'),
        schoolId: Yup.string().required('School is required'),
        publishStatusKeys: Yup.array()
            .of(Yup.string())
            .min(1, 'Select at least one status to publish')
            .required('Select at least one status to publish'),
    });
   
    const editSchedule = (scheduleItem) => {
        const keys = Array.isArray(scheduleItem.publishStatusKeys) && scheduleItem.publishStatusKeys.length
            ? scheduleItem.publishStatusKeys
            : [...DEFAULT_PUBLISH_STATUS_KEYS];
        setSchedule({
            ...scheduleItem,
            schoolId: scheduleItem.schoolId?._id || scheduleItem.schoolId || '',
            isNotifyBeforePublish: Boolean(scheduleItem.isNotifyBeforePublish),
            publishStatusKeys: keys,
        });
        setOpen(true);
    };

    const deleteSchedule = async (scheduleDelete) => {
        if (scheduleDelete && await showAppConfirm('Are you sure you want to delete this schedule?')) {
            ScheduleService.deleteSchedule(scheduleDelete).then((res) => {
                getScheduleList();
                getRegisteredSchoolsCount();
                alert('Schedule deleted successfully.');
            }).catch((err) => {
                alert('Error deleting schedule: ' + (err.response?.data?.message || err.message));
            });
        }
    };

    const formatFrequency = (cronExpression) => {
        if (!cronExpression) return 'N/A';

        const frequencyMap = {
            // '* * * * *': 'Every 5 Minute',
            '0 0 * * 5': 'Weekly on Friday',
            EVERY_2_WEEKS_FRIDAY: 'Every 2 Weeks on Friday',
            '0 0 1 * *': 'Monthly on 1st',
            '0 0 15 * *': 'Monthly on 15th',
        };

        return frequencyMap[cronExpression] || cronExpression;
    };


    const formik = useFormik({
        initialValues: schedule,
        enableReinitialize: true,
        validationSchema: validationSchema,
        onSubmit: (values, { resetForm }) => {
            const submitData = {
                ...values,
                isNotifyBeforePublish: Boolean(values.isNotifyBeforePublish),
                publishStatusKeys: Array.isArray(values.publishStatusKeys)
                    ? values.publishStatusKeys
                    : [...DEFAULT_PUBLISH_STATUS_KEYS],
            };

            if(schedule._id){
                ScheduleService.updateSchedule({ ...submitData, _id: schedule._id }).then((res) => {
                    handleClose();
                    getScheduleList();
                    getRegisteredSchoolsCount();
                    resetForm();
                    alert("Schedule Updated Successfully.");
                }).catch((err) => {
                    alert(err.response?.data?.message || 'Error updating schedule');
                });
            } else {
                ScheduleService.createSchedule(submitData).then((res) => {
                    getScheduleList();
                    getRegisteredSchoolsCount();
                    handleClose();
                    resetForm();
                    alert("Schedule Added Successfully.");
                }).catch((err) => {
                    alert(err.response?.data?.message || 'Error creating schedule');
                });
            }
        },
    });

    return (
        <>
            <PageTitle title="Schedule" button={<Button
                variant="contained" 
                onClick={() => handleOpen(true)}
                size="medium"
                color="secondary" 
                style={{backgroundColor:'rgb(15 59 98)'}}> 
                Add Schedule
            </Button>} />
            <Grid container spacing={4}>
                <Grid item xs={12}>
                    <Widget title="About Schedule" upperTitle>
                        <div style={{ padding: 16 }}>
                            <Typography variant="body1" style={{ fontWeight: 700, color: "#1a1a1a" }}>
                                What is Schedule?
                            </Typography>
                            <Typography variant="body2" color="textSecondary" style={{ marginTop: 6 }}>
                                The schedule automatically publishes planned activities to the Parent module according to the selected frequency (weekly, bi-weekly, monthly, etc.).
                            </Typography>

                            <Typography variant="body1" style={{ fontWeight: 700, color: "#1a1a1a", marginTop: 14 }}>
                                How to use it
                            </Typography>
                            <Typography variant="body2" color="textSecondary" style={{ marginTop: 6 }}>
                                Choose the frequency, school, and which activity statuses to publish. When the schedule runs, only draft activities matching those statuses are published to parents.
                            </Typography>

                            <Typography variant="body1" style={{ fontWeight: 700, color: "#1a1a1a", marginTop: 14 }}>
                                Publish statuses (multi-select)
                            </Typography>
                            <Typography variant="body2" color="textSecondary" style={{ marginTop: 6, lineHeight: 1.6 }}>
                                Select one or more: Only presented, Only work with assistance, Only work independently, Mastered.
                                Activities with other statuses stay in draft until their status is selected on the schedule.
                            </Typography>

                            <Typography variant="body1" style={{ fontWeight: 700, color: "#1a1a1a", marginTop: 14 }}>
                                Notify before publishing (approval required)
                            </Typography>
                            <Typography variant="body2" color="textSecondary" style={{ marginTop: 6, lineHeight: 1.6 }}>
                                If <b>No</b>: Matching activities are automatically published to parents when the schedule runs.
                                <br />
                                <br />
                                If <b>Yes</b>: Matching activities remain in draft. The School Admin receives a header notification to approve them, and they are published to parents only after approval.
                            </Typography>
                        </div>
                    </Widget>
                </Grid>
                {/* <Grid item xs={12} md={4} sm={6}>
                    <Widget
                        title={`Registered Schools (${registeredSchoolsCount})`}
                        upperTitle
                    >
                        <div style={{ padding: '20px' }}>
                            <FormControl variant="outlined" fullWidth>
                                <InputLabel shrink id="registered-schools-label">
                                    Select School
                                </InputLabel>
                                <Select
                                    labelId="registered-schools-label"
                                    id="registered-schools-select"
                                    label="Select School"
                                    value={selectedRegisteredSchool}
                                    displayEmpty
                                    onChange={(e) => setSelectedRegisteredSchool(e.target.value)}
                                    renderValue={(val) => (val ? val : <em>Select a registered school</em>)}
                                >
                                    <MenuItem value="">
                                        <em>Select a registered school</em>
                                    </MenuItem>
                                    {registeredSchoolNames.length > 0 ? (
                                        registeredSchoolNames.map((schoolName, index) => (
                                            <MenuItem key={index} value={schoolName}>
                                                {schoolName}
                                            </MenuItem>
                                        ))
                                    ) : (
                                        <MenuItem value="" disabled>
                                            <em>No schools registered yet</em>
                                        </MenuItem>
                                    )}
                                </Select>
                            </FormControl>
                            {registeredSchoolNames.length > 0 && (
                                <Typography variant="body2" color="textSecondary" style={{ marginTop: '16px', textAlign: 'center' }}>
                                    {registeredSchoolsCount} {registeredSchoolsCount === 1 ? 'school' : 'schools'} registered
                                </Typography>
                            )}
                        </div>
                    </Widget>
                </Grid> */}
                <Grid item xs={12}>
                    <Widget title="" upperTitle noBodyPadding bodyClass={classes.tableOverflow}>
                        <TableContainer component={Paper} style={{ maxHeight: 500 }}>
                            <Table stickyHeader className="mb-0">
                                <TableHead>
                                    <TableRow>
                                        {tableHeaders.map(key => (
                                            <StyledTableCell key={key}>{key}</StyledTableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {scheduleList.map((scheduleItem) => {
                                        const statusKeys =
                                            Array.isArray(scheduleItem.publishStatusKeys) &&
                                            scheduleItem.publishStatusKeys.length
                                                ? scheduleItem.publishStatusKeys
                                                : DEFAULT_PUBLISH_STATUS_KEYS;
                                        return (
                                        <TableRow key={scheduleItem._id}>
                                            <TableCell className="pl-3 fw-normal">
                                                <div>{formatFrequency(scheduleItem.frequency)}</div>
                                                {!String(scheduleItem.frequency || '').startsWith('EVERY_') && (
                                                    <div style={{ fontSize: '0.75rem', color: '#666' }}>{scheduleItem.frequency}</div>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {getScheduleSchoolName(scheduleItem, schoolList)}
                                            </TableCell>
                                            <TableCell>
                                                <div className={classes.chipsWrap}>
                                                    {statusKeys.map((key) => (
                                                        <Chip
                                                            key={key}
                                                            size="small"
                                                            label={publishStatusLabel(key)}
                                                            className={classes.chip}
                                                        />
                                                    ))}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                {Boolean(scheduleItem.isNotifyBeforePublish) ? 'Yes' : 'No'}
                                            </TableCell>
                                            <TableCell>
                                                <EditIcon 
                                                    style={{ cursor: 'pointer' }} 
                                                    onClick={() => editSchedule(scheduleItem)} 
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <DeleteIcon 
                                                    style={{ cursor: 'pointer' }} 
                                                    onClick={() => deleteSchedule(scheduleItem)} 
                                                />
                                            </TableCell>
                                        </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Widget>
                </Grid>
            </Grid>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="xs"
                fullWidth
                scroll="body"
                PaperProps={{
                    style: {
                        borderRadius: 14,
                        maxWidth: 440,
                        width: '100%',
                        margin: 16,
                    },
                }}
            >
                <DialogTitle
                    disableTypography
                    style={{ padding: '16px 20px 4px', fontWeight: 800, color: '#1a1a1a', fontSize: '1.15rem' }}
                >
                    {schedule._id ? 'Edit Schedule' : 'Add Schedule'}
                </DialogTitle>
                <form onSubmit={formik.handleSubmit} style={{ display: 'contents' }}>
                    <DialogContent style={{ padding: '8px 20px 4px', overflow: 'visible' }}>
                        <Grid container spacing={1}>
                            <Grid item xs={12}>
                                <FormControl variant="outlined" fullWidth margin="dense">
                                    <InputLabel id="frequency-label">Frequency (Cron Schedule)</InputLabel>
                                    <Select
                                        labelId="frequency-label"
                                        id="frequency"
                                        name="frequency"
                                        value={formik.values.frequency}
                                        onChange={formik.handleChange}
                                        error={formik.touched.frequency && Boolean(formik.errors.frequency)}
                                        label="Frequency (Cron Schedule)"
                                    >
                                        <MenuItem value="">
                                            <em>None</em>
                                        </MenuItem>
                                        {/* <MenuItem value="* * * * *">Every 5 Minute</MenuItem> */}
                                        <MenuItem value="0 0 * * 5">Weekly on Friday</MenuItem>
                                        <MenuItem value="EVERY_2_WEEKS_FRIDAY">Every 2 Weeks on Friday</MenuItem>
                                        <MenuItem value="0 0 1 * *">Monthly on 1st</MenuItem>
                                        <MenuItem value="0 0 15 * *">Monthly on 15th</MenuItem>
                                    </Select>
                                    {formik.touched.frequency && formik.errors.frequency && (
                                        <div style={{ color: "red", fontSize: "0.75rem", marginTop: 4 }}>
                                            {formik.errors.frequency}
                                        </div>
                                    )}
                                    {formik.values.frequency && (
                                        <div style={{ fontSize: "0.75rem", color: "#666", marginTop: 4 }}>
                                            {formik.values.frequency.startsWith("EVERY_")
                                                ? `Schedule: ${formatFrequency(formik.values.frequency)}`
                                                : `Cron: ${formik.values.frequency}`}
                                        </div>
                                    )}
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <FormControl variant="outlined" fullWidth margin="dense">
                                    <InputLabel id="school-label">School</InputLabel>
                                    <Select
                                        labelId="school-label"
                                        id="schoolId"
                                        name="schoolId"
                                        value={formik.values.schoolId}
                                        onChange={formik.handleChange}
                                        error={formik.touched.schoolId && Boolean(formik.errors.schoolId)}
                                        disabled={isSchoolRole && Boolean(currentSchoolId)}
                                        label="School"
                                    >
                                        <MenuItem value="">
                                            <em>None</em>
                                        </MenuItem>
                                        {schoolList.map((school) => (
                                            <MenuItem key={school._id} value={school._id}>
                                                {school.schooleName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {formik.touched.schoolId && formik.errors.schoolId && (
                                        <div style={{ color: "red", fontSize: "0.75rem", marginTop: 4 }}>
                                            {formik.errors.schoolId}
                                        </div>
                                    )}
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <FormControl
                                    variant="outlined"
                                    fullWidth
                                    margin="dense"
                                    error={formik.touched.publishStatusKeys && Boolean(formik.errors.publishStatusKeys)}
                                >
                                    <InputLabel id="publish-status-label">Publish to parents (statuses)</InputLabel>
                                    <Select
                                        labelId="publish-status-label"
                                        id="publishStatusKeys"
                                        name="publishStatusKeys"
                                        multiple
                                        value={formik.values.publishStatusKeys || []}
                                        onChange={formik.handleChange}
                                        label="Publish to parents (statuses)"
                                        renderValue={(selected) =>
                                            (selected || []).map(publishStatusLabel).join(', ')
                                        }
                                    >
                                        {PUBLISH_STATUS_OPTIONS.map((opt) => (
                                            <MenuItem key={opt.value} value={opt.value}>
                                                <Checkbox
                                                    color="primary"
                                                    checked={(formik.values.publishStatusKeys || []).indexOf(opt.value) > -1}
                                                />
                                                <ListItemText primary={opt.label} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {formik.touched.publishStatusKeys && formik.errors.publishStatusKeys && (
                                        <div style={{ color: "red", fontSize: "0.75rem", marginTop: 4 }}>
                                            {formik.errors.publishStatusKeys}
                                        </div>
                                    )}
                                    <div style={{ fontSize: "0.75rem", color: "#666", marginTop: 4 }}>
                                        Only draft activities with these statuses will be published to parents.
                                    </div>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            color="primary"
                                            checked={Boolean(formik.values.isNotifyBeforePublish)}
                                            onChange={(e) => formik.setFieldValue('isNotifyBeforePublish', e.target.checked)}
                                        />
                                    }
                                    label="Notify before publishing (approval required)"
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions style={{ padding: '8px 20px 16px', margin: 0 }}>
                        <Button onClick={handleClose} variant="outlined" size="small">
                            Cancel
                        </Button>
                        <Button type="submit" color="primary" variant="contained" size="small">
                            Save
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </>
    );
}
